import type { ReactNode } from "react";
import { Alert } from "../Alert/Alert";
import { AuthHeader } from "../AuthHeader/AuthHeader";
import { AuthLayout } from "../AuthLayout/AuthLayout";
import { Button } from "../Button/Button";
import { Checkbox } from "../Checkbox/Checkbox";
import { Field } from "../Field/Field";
import { PasswordInput } from "../PasswordInput/PasswordInput";
import { TextInput } from "../TextInput/TextInput";
import { rememberMeLabel } from "./loginForm";
import { useLoginForm } from "./useLoginForm";

export interface LoginScreenProps {
  /** {@code rememberMe} is true only when the "Keep me signed in" box is shown and ticked. */
  onSubmit: (values: { email: string; password: string; rememberMe: boolean }) => void;
  loading?: boolean;
  error?: string;
  /** Tone of the error (warning for a locked account). */
  errorTone?: "danger" | "warning";
  errorTitle?: string;
  /** Informational message above the form (e.g. expired session). */
  notice?: ReactNode;
  /** Link next to the password label (e.g. "Forgot your password?"). */
  forgotLink?: ReactNode;
  footer?: ReactNode;
  email?: string;
  disabled?: boolean;
  /** Days offered by "Keep me signed in"; the box is hidden when 0 or absent. */
  rememberMeDays?: number;
}

/** Login form on the shared auth layout. */
export function LoginScreen({
  onSubmit,
  loading,
  error,
  errorTone = "danger",
  errorTitle,
  notice,
  forgotLink,
  footer,
  email: initialEmail = "",
  disabled,
  rememberMeDays = 0,
}: LoginScreenProps): React.JSX.Element {
  const offerRememberMe = rememberMeDays > 0;
  const form = useLoginForm(initialEmail, offerRememberMe, onSubmit);

  return (
    <AuthLayout>
      <AuthHeader title="Sign in" lead="Use your Nabble Suite account to continue." />
      <div className="nb-auth-content">
        {notice}
        <form className="nb-auth-form" onSubmit={form.handleSubmit} noValidate>
          <Field label="Email" error={form.missing.email}>
            <TextInput
              icon="mail"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              autoFocus={!initialEmail}
              value={form.email}
              onChange={(e) => form.setEmail(e.target.value)}
            />
          </Field>
          <Field label="Password" labelAside={forgotLink} error={form.missing.password}>
            <PasswordInput
              name="password"
              autoComplete="current-password"
              required
              value={form.password}
              onChange={(e) => form.setPassword(e.target.value)}
            />
          </Field>
          {offerRememberMe ? (
            <Checkbox
              name="rememberMe"
              label={rememberMeLabel(rememberMeDays)}
              checked={form.rememberMe}
              onChange={(e) => form.setRememberMe(e.target.checked)}
            />
          ) : null}
          {error ? (
            <Alert key={form.attempt} tone={errorTone} title={errorTitle} shaking={errorTone === "danger"}>
              {error}
            </Alert>
          ) : null}
          <Button type="submit" label="Sign in" size="lg" block loading={loading} disabled={disabled} />
        </form>
        {footer}
      </div>
    </AuthLayout>
  );
}
